'use strict';
import LANGUAGES from '@aretheyhappy/lib-taxonomy/global/language';
import { isNeObject, isString } from '@aretheyhappy/utils';
import I18NCore from './I18NCore';
import Flash from './Flash';
export const LOCAL_STORAGE = {
    KEY: 'ath-lng',
};
//  Utility function: check if a language is recognized
const isRecognizedLanguage = (lang) => !!(Object.values(LANGUAGES).indexOf(lang) > -1);
//  Store in local storage
const storeLng = (lng) => {
    if (localStorage !== undefined)
        localStorage.setItem(LOCAL_STORAGE.KEY, lng);
};
//  Retrieve from local storage
const retrieveLng = () => {
    //  If we have a local storage lang available -> return that
    if (typeof localStorage !== 'undefined' && localStorage.getItem(LOCAL_STORAGE.KEY)) {
        return localStorage.getItem(LOCAL_STORAGE.KEY);
    }
    //  If navigator language is recognized as one of our lang packs -> return that
    const navlang = (window.navigator.language).substring(0, 2).toLowerCase();
    if (isRecognizedLanguage(navlang)) {
        return navlang;
    }
    //  Fallback to english
    return 'en';
};
export const EVT_I18N = {
    CHANGE_START: 'ath:i18n:change_start',
    CHANGE_END: 'ath:i18n:change_end',
    CHANGE_SUCCESS: 'ath:i18n:change_success',
    CHANGE_ERROR: 'ath:i18n:change_error',
};
/**
 * Provides localization functions and support
 */
class I18NClass {
    static getInstance() {
        if (!I18NClass.instance) {
            I18NClass.instance = new I18NClass();
        }
        return I18NClass.instance;
    }
    constructor() {
        this.is_ready = false;
        this.lng = 'en';
        this.fallback = 'en';
        this.version = '';
        this.is_ready = false;
        this.lng = retrieveLng();
        this.fallback = 'en';
        // Bind this directly to the functions so that we they are
        // used as saved functions they retain scope.
        // (for example the athT filter)
        this.boot = this.boot.bind(this);
        this.locale = this.locale.bind(this);
        this.go = this.go.bind(this);
        I18NCore.initialize({ fallback_locale: this.fallback });
    }
    get current() {
        return this.lng;
    }
    get default() {
        return this.fallback;
    }
    recognizedLanguage(lang) {
        return isRecognizedLanguage(lang);
    }
    /**
     * Load the current and fallback locale packs
     * @param options Boot options
     * @param options.version Cache-busting version appended to locale pack requests (eg: the consumer's package version)
     */
    async boot(options = {}) {
        if (this.is_ready)
            return;
        if (isString(options.version))
            this.version = options.version;
        //  Load language
        await this.locale(this.lng);
        //  Load fallback
        await this.locale(this.fallback, false);
        this.is_ready = true;
    }
    /**
     * Build the url of a locale pack, suffixed with the boot version when one was provided
     * @param locale Locale to build the url for
     * @returns
     */
    localeUrl(locale) {
        const url = `/assets/locales/${locale}.json`;
        return this.version ? `${url}?v=${encodeURIComponent(this.version)}` : url;
    }
    async locale(new_locale, set = true) {
        try {
            Flash.publish(EVT_I18N.CHANGE_START);
            let data = false;
            //  Load file
            await fetch(this.localeUrl(new_locale))
                .then(resp => {
                if (resp.ok)
                    return resp.json();
                return new Promise(resolve => resolve({}));
            })
                .then((val = {}) => data = val);
            //  Extend I18N core with locale
            if (isNeObject(data))
                I18NCore.extendLocale(new_locale, data);
            //  Should we set or not
            if (set) {
                this.lng = new_locale;
                storeLng(this.lng);
                //  Update document language
                document.documentElement.setAttribute('lang', this.lng);
            }
            Flash.publish(EVT_I18N.CHANGE_END);
        }
        catch {
            Flash.publish(EVT_I18N.CHANGE_ERROR);
        }
    }
    /**
     * Verify if a translation key exists on the map
     * @param key I18N language key
     * @returns
     */
    hasKey(key) {
        if (!this.is_ready)
            return false;
        //  If found in the locale map
        if (I18NCore.has(this.lng, key))
            return true;
        //  If found in the fallback locale map
        if (I18NCore.has(this.fallback, key))
            return true;
        return false;
    }
    /**
     * Translate the provided key
     * @param key I18N language key
     * @param params Parameters to provide to the I18N language key
     * @param nullable (default=false) If set to true will return null if serialization not found
     * @returns
     */
    go(key, params = {}) {
        if (!this.is_ready)
            return '';
        //  Check for key not being string
        if (!isString(key))
            return '';
        return I18NCore.run(this.lng, key, params);
    }
    /**
     * Translate a specific entity action
     * example: action('duplicate', 'caption')
     * @param act The action to translate
     * @param entity The entity on which the action was run
     * @deprecated Stop using this. This requires Mead to make assumptions about the locale structure defined by the consumer.
     * @returns
     */
    action(act, entity) {
        return this.go(`general.entity_acts.${act}`, { name: this.go(`general.entities.${entity}`) });
    }
    /**
     * Translate a specific entity action, but lowercase the entity
     * example: actionLc('duplicate', 'caption')
     * @param act The action to translate
     * @param entity The entity on which the action was run
     * @deprecated Stop using this. This requires Mead to make assumptions about the locale structure defined by the consumer.
     * @returns
     */
    actionLc(act, entity) {
        return this.go(`general.entity_acts.${act}`, { name: this.go(`general.entities.${entity}`).toLowerCase() });
    }
}
const I18N = I18NClass.getInstance();
export { I18N };
export default I18N;
//# sourceMappingURL=I18N.js.map